import { normalizeMediaDataUrl } from '@/utils/inspectionDisplay';

function getStoredAuthToken() {
  if (typeof window === 'undefined') return '';
  return (
    localStorage.getItem('admin_token') ||
    localStorage.getItem('session_token') ||
    localStorage.getItem('token') ||
    ''
  );
}

function resolveBackendOrigin() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

/** Absolute URL for /api/... receipt endpoints returned by admin enrichment. */
function resolveReceiptFetchUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (
    trimmed.startsWith('data:') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.replace(/\s/g, '').startsWith('JVBERi')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('/')) {
    const base = resolveBackendOrigin();
    return base ? `${base}${trimmed}` : trimmed;
  }
  return trimmed;
}

/** Resolve receipt entry (string or mobile app { url, type, id }) to a displayable src. */
export function resolveExpenseReceiptSrc(receipt) {
  if (receipt == null) return '';
  if (typeof receipt === 'string') {
    if (receipt.startsWith('/api/')) {
      return resolveReceiptFetchUrl(receipt);
    }
    return normalizeMediaDataUrl(receipt);
  }
  if (typeof receipt === 'object') {
    const raw = receipt.url || receipt.data_url || receipt.photo_data || receipt.data;
    if (typeof raw === 'string' && raw.startsWith('/api/')) {
      return resolveReceiptFetchUrl(raw);
    }
    return normalizeMediaDataUrl(raw);
  }
  return '';
}

export function isPdfReceiptSrc(src) {
  if (!src || typeof src !== 'string') return false;
  const trimmed = src.trim();
  // Only inspect the data: header — a base64 JPEG body almost always contains "pdf" by chance.
  if (trimmed.toLowerCase().startsWith('data:')) {
    const comma = trimmed.indexOf(',');
    const header = (comma >= 0 ? trimmed.slice(0, comma) : trimmed.slice(0, 64)).toLowerCase();
    return header.includes('application/pdf');
  }
  if (trimmed.startsWith('JVBERi')) return true; // raw %PDF base64 without data: prefix
  return trimmed.toLowerCase().split('?')[0].endsWith('.pdf');
}

/**
 * Convert a data: URL to a Blob without fetch() — large PDF data URLs often fail
 * or hang with fetch() on mobile Chrome / Safari.
 */
export function dataUrlToBlob(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') {
    throw new Error('empty data url');
  }
  const comma = dataUrl.indexOf(',');
  if (comma < 0) {
    // bare base64 PDF
    if (dataUrl.replace(/\s/g, '').startsWith('JVBERi')) {
      const compact = dataUrl.replace(/\s/g, '');
      const binary = atob(compact);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: 'application/pdf' });
    }
    throw new Error('invalid data url');
  }
  const header = dataUrl.slice(0, comma);
  const payload = dataUrl.slice(comma + 1);
  const mimeMatch = header.match(/^data:([^;,]+)/i);
  const mime = (mimeMatch && mimeMatch[1]) || 'application/octet-stream';
  const isBase64 = /;base64/i.test(header);

  if (isBase64) {
    const compact = payload.replace(/\s/g, '');
    const binary = atob(compact);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    // Chunked copy keeps UI responsive on multi-MB receipts
    const CHUNK = 0x8000;
    for (let offset = 0; offset < len; offset += CHUNK) {
      const end = Math.min(offset + CHUNK, len);
      for (let i = offset; i < end; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
    }
    return new Blob([bytes], { type: mime });
  }

  return new Blob([decodeURIComponent(payload)], { type: mime });
}

/** Needs an authenticated fetch (backend /api receipt endpoint) — a plain <img src> would 401. */
export function receiptNeedsAuthFetch(url) {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('/api/') || url.includes('/api/admin/expense-reports/');
}

/** Resolve a receipt URL to { objectUrl, blob, revoke } for in-app preview (img / iframe). */
export async function loadExpenseReceiptObjectUrl(url) {
  const blob = await urlToBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  return { objectUrl, blob, revoke: () => URL.revokeObjectURL(objectUrl) };
}

async function urlToBlob(url) {
  const resolved = resolveReceiptFetchUrl(url);
  if (!resolved) throw new Error('empty url');

  if (resolved.startsWith('data:') || resolved.replace(/\s/g, '').startsWith('JVBERi')) {
    return dataUrlToBlob(
      resolved.startsWith('data:')
        ? resolved
        : `data:application/pdf;base64,${resolved.replace(/\s/g, '')}`,
    );
  }

  const headers = {};
  const token = getStoredAuthToken();
  if (token && (resolved.includes('/api/') || resolved.startsWith('/'))) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(resolved, { headers, cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

/**
 * Open a receipt in a new tab. Browsers often block target=_blank on large
 * data:application/pdf URLs — convert to a blob: URL first (via atob, not fetch).
 * Falls back to same-tab open, then forced download.
 */
export async function openExpenseReceipt(url, { downloadName, forceDownload = false } = {}) {
  if (!url || typeof url !== 'string') return false;
  let revoke = null;
  // Open the tab synchronously (still inside the click) so popup blockers allow it.
  // Don't pass 'noopener' — window.open then always returns null and we'd fall back to a download.
  let pendingWin = null;
  if (!forceDownload) {
    try {
      pendingWin = window.open('', '_blank');
    } catch (_) {
      pendingWin = null;
    }
  }
  try {
    const blob = await urlToBlob(url);
    const blobUrl = URL.createObjectURL(blob);
    revoke = () => URL.revokeObjectURL(blobUrl);

    const name =
      downloadName ||
      (isPdfReceiptSrc(url) || blob.type === 'application/pdf'
        ? 'ticket-autogo.pdf'
        : 'ticket-autogo.jpg');

    if (forceDownload) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(revoke, 120_000);
      return true;
    }

    if (pendingWin && !pendingWin.closed) {
      try {
        pendingWin.opener = null;
      } catch (_) {
        /* ignore */
      }
      pendingWin.location.href = blobUrl;
    } else {
      // Popup blocked — try download so the admin can still open the file.
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(revoke, 120_000);
    return true;
  } catch (err) {
    console.error('openExpenseReceipt failed:', err);
    if (pendingWin && !pendingWin.closed) {
      try {
        pendingWin.close();
      } catch (_) {
        /* ignore */
      }
    }
    if (revoke) {
      try {
        revoke();
      } catch (_) {
        /* ignore */
      }
    }
    return false;
  }
}

/** Prefer API-normalized receipt_items / receipt_urls; fall back to raw receipts[]. */
export function getExpenseReceiptItems(report) {
  if (!report || typeof report !== 'object') return [];

  if (Array.isArray(report.receipt_items) && report.receipt_items.length > 0) {
    return report.receipt_items
      .map((item) => ({
        url: item.url || '',
        // Sharpened full-size image for the admin preview (originals stay on url / download).
        viewUrl: item.view_url || '',
        type: item.type || '',
        isPdf: Boolean(item.is_pdf) || isPdfReceiptSrc(item.url),
        id: item.id,
      }))
      .filter((item) => item.url);
  }

  if (Array.isArray(report.receipt_urls) && report.receipt_urls.length > 0) {
    return report.receipt_urls.map((url, idx) => {
      const raw = report.receipts?.[idx];
      const type = typeof raw === 'object' ? raw.type : '';
      return { url, type, isPdf: isPdfReceiptSrc(url), id: raw?.id };
    });
  }

  return (report.receipts || [])
    .map((r, idx) => {
      const url = resolveExpenseReceiptSrc(r);
      const type = typeof r === 'object' ? r.type || '' : '';
      return { url, type, isPdf: isPdfReceiptSrc(url), id: typeof r === 'object' ? r.id : undefined };
    })
    .filter((item) => item.url);
}

export function formatExpenseReportDate(report, dateLocale = 'es-ES') {
  const raw = report?.report_date || report?.submitted_at || report?.created_at;
  if (!raw) return null;
  const d = new Date(typeof raw === 'string' ? raw.replace('Z', '') : raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(dateLocale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatServiceDateRange(fromVal, toVal, dateLocale = 'es-ES') {
  const fmt = (v) => {
    if (!v) return null;
    const d = new Date(typeof v === 'string' ? v.replace('Z', '') : v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const a = fmt(fromVal);
  const b = fmt(toVal);
  if (a && b && a !== b) return `${a} – ${b}`;
  return a || b || null;
}
